import { useQuery } from '@apollo/client';
import { formatDistanceToNow } from 'date-fns';
import React, { FC, useMemo } from 'react';
import Avatar from '@atlaskit/avatar';
import AtlasComment, { CommentAuthor, CommentTime } from '@atlaskit/comment';
import EmptyState from '@atlaskit/empty-state';
import Tooltip from '@atlaskit/tooltip';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { Comment } from '@css-xp/apd-common';
import { ErrorComponents, FormattedDate } from '@css-xp/base';
import { useApplicationContext } from '@css-xp/base/common-context';
import { fetchTaskComments } from '../../../queries/tasks';
import './comments-tab.scss';

interface CommentsProps {
  taskId: string;
}

export const noCommentHeader = 'No Comments Yet';
export const noCommentDesc = 'Comments on this request will appear here.';
export const commentError = 'Failed to load comments.';

export const CommentsTab: FC<CommentsProps> = ({ taskId }) => {
  const userProfile = useApplicationContext();

  const { data, loading, error } = useQuery<{ comments: Comment[] }>(fetchTaskComments, {
    skip: !userProfile?.userId,
    variables: {
      taskId: taskId,
      id: userProfile?.userId?.split('@')[0],
    },
  });
  const comments = (data?.comments ?? []).map(({ id, userId, time, message }) => ({
    id,
    author: userId,
    time,
    content: message,
    avatarImg: undefined,
  }));
  const visibleComments = useMemo(() => {
    const _comments = [...comments];
    return _comments.sort((c1, c2) => {
      const c1Time = c1.time ? new Date(c1.time).getTime() : 0;
      const c2Time = c2.time ? new Date(c2.time).getTime() : 0;
      return c2Time - c1Time;
    });
  }, [comments]);

  if (loading) return <LoadingIndicator />;

  if (error) {
    return <ErrorComponents.ErrorPanel error={new Error(`${commentError} ${error?.message}`)} />;
  }

  if (!comments.length) {
    return <EmptyState header={noCommentHeader} description={noCommentDesc} />;
  }

  return (
    <div className="comments-tab">
      {visibleComments.map(({ id, avatarImg, author, time, content }) => (
        <AtlasComment
          key={id}
          avatar={<Avatar src={avatarImg} size="medium" />}
          author={<CommentAuthor>{author}</CommentAuthor>}
          time={
            <Tooltip content={<FormattedDate date={time} showTime />}>
              <CommentTime>{formatDistanceToNow(time ? new Date(time).getTime() : 0, { addSuffix: true })}</CommentTime>
            </Tooltip>
          }
          content={<p>{content}</p>}
        />
      ))}
    </div>
  );
};
